import React from 'react';
import { motion } from 'framer-motion';
interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
  label?: string;
}
interface CompositeShapeProps {
  rectangles: Rectangle[];
  unit?: string;
  label?: string;
  showGrid?: boolean;
  showDimensions?: boolean;
  mode?: 'stimulus' | 'support';
}
const RECT_COLORS = [{
  fill: 'rgb(191, 219, 254)',
  stroke: 'rgb(59, 130, 246)'
}, {
  fill: 'rgb(187, 247, 208)',
  stroke: 'rgb(34, 197, 94)'
}, {
  fill: 'rgb(254, 215, 170)',
  stroke: 'rgb(249, 115, 22)'
}, {
  fill: 'rgb(233, 213, 255)',
  stroke: 'rgb(168, 85, 247)'
} // purple
];
export function CompositeShape({
  rectangles,
  unit = 'cm',
  label,
  showGrid = true,
  showDimensions = true,
  mode = 'stimulus'
}: CompositeShapeProps) {
  const cellSize = 24;
  const padding = 30; // Reduced padding
  // Calculate bounding box
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  rectangles.forEach(rect => {
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.width);
    maxY = Math.max(maxY, rect.y + rect.height);
  });
  const gridWidth = maxX - minX;
  const gridHeight = maxY - minY;
  const svgWidth = gridWidth * cellSize + padding * 2;
  const svgHeight = gridHeight * cellSize + padding * 2;
  // Convert grid coordinates to SVG coordinates
  const toSvgX = (x: number) => padding + (x - minX) * cellSize;
  // Y is flipped so 0 is at bottom
  const toSvgYFlipped = (y: number) => svgHeight - padding - (y - minY) * cellSize;
  // Calculate total area
  const totalArea = rectangles.reduce((sum, rect) => sum + rect.width * rect.height, 0);
  return <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 w-full overflow-hidden" data-id="element-12665">
      {label && <div className="text-sm font-medium text-slate-700 mb-3 text-center" data-id="element-12666">
          {label}
        </div>}
      <div className="flex justify-center w-full" data-id="element-12667">
        <motion.svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{
        width: '100%',
        height: 'auto',
        maxWidth: svgWidth
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-12668">
          {/* Rectangles */}
          {rectangles.map((rect, index) => {
          const colors = RECT_COLORS[index % RECT_COLORS.length];
          const x = toSvgX(rect.x);
          const y = toSvgYFlipped(rect.y + rect.height);
          const width = rect.width * cellSize;
          const height = rect.height * cellSize;
          return <motion.g key={index} initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: index * 0.15,
            duration: 0.3
          }} data-id="element-12669">
                {/* Rectangle fill */}
                <rect x={x} y={y} width={width} height={height} fill={colors.fill} stroke={colors.stroke} strokeWidth="2" data-id="element-12670" />

                {/* Grid cells */}
                {showGrid && Array.from({
              length: rect.height
            }).map((_, row) => Array.from({
              length: rect.width
            }).map((_, col) => <rect key={`${index}-${row}-${col}`} x={x + col * cellSize} y={y + row * cellSize} width={cellSize} height={cellSize} fill="none" stroke={colors.stroke} strokeWidth="0.5" opacity={0.4} data-id="element-12671" />))}

                {/* Dimension labels */}
                {showDimensions && <>
                    {/* Width label (top) */}
                    <text x={x + width / 2} y={y - 6} textAnchor="middle" className="text-[10px] font-bold" fill={colors.stroke} data-id="element-12672">
                      {rect.width} {unit}
                    </text>

                    {/* Height label (left) */}
                    <text x={x - 6} y={y + height / 2} textAnchor="end" dominantBaseline="middle" className="text-[10px] font-bold" fill={colors.stroke} data-id="element-12673">
                      {rect.height} {unit}
                    </text>
                  </>}

                {/* Rectangle label (center) */}
                {rect.label && <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="middle" className="text-xs font-bold" fill={colors.stroke} style={{
              textShadow: '0px 0px 2px rgba(255,255,255,0.8)'
            }} data-id="element-12674">
                    {rect.label}
                  </text>}
              </motion.g>;
        })}
        </motion.svg>
      </div>

      {/* Area calculation (only in support mode) */}
      {mode === 'support' && <div className="mt-4 text-center text-sm text-slate-600 space-y-1" data-id="element-12675">
          {rectangles.map((rect, index) => <p key={index} data-id="element-12676">
              {rect.label || `Rechthoek ${index + 1}`}: {rect.width} ×{' '}
              {rect.height} = {rect.width * rect.height} {unit}²
            </p>)}
          <p className="font-semibold text-slate-700 pt-1 border-t border-slate-200" data-id="element-12677">
            Totaal: {totalArea} {unit}²
          </p>
        </div>}
    </div>;
}